import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { AutoComplete } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useFormContext } from 'react-hook-form';
import { VetPracticesSearchResult } from './vetPracticesSearchResult';
import { isRequired } from '../../../utils/validationUtils';
import './vetPracticesSearch.scss';
import Loading from '../../shared/Loading/Loading';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { useVetPracticeSearch } from './useVetPracticeSearch';
import { VET_PRACTICE_SEARCH_SRINSTRUCTION } from '../../../constants/constants';
import ErrorText from '../../shared/ErrorText/ErrorText';
import { createAlias } from '../../../utils/alias';
import { BrandContext } from '../../../contextProviders/brandContext';

export const VetPracticesSearch = ({
  onSelect,
  selectedPets,
  activeVet,
  showAlreadyLinkedError,
  setShowAlreadyLinkedError
}) => {
  const formContext = useFormContext();
  const { isVetSelected, searchResults, isLoading, handleSearch, handleSelect, showNoResultsError } =
    useVetPracticeSearch(onSelect, selectedPets, activeVet, showAlreadyLinkedError, setShowAlreadyLinkedError);
  const {
    activeBrandContext: {
      cspConfiguration: { faqRedirection }
    }
  } = useContext(BrandContext);

  const addVetFaqUrl = `${CSPUrlsMap.helpCentre.url}?category=${createAlias(
    faqRedirection?.addYourVet?.category
  )}&question=${createAlias(faqRedirection?.addYourVet?.question)}`;

  return (
    <div className="vetPracticesSearch">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <AutoComplete
            name="practiceName"
            minCharacters={1}
            label="Practice name"
            placeholder="Search for your vet"
            fieldFormContext={formContext}
            validation={{
              required: showNoResultsError || showAlreadyLinkedError ? false : isRequired('Please input vet name.')
            }}
            defaultValue=""
            srInstruction={VET_PRACTICE_SEARCH_SRINSTRUCTION}
            ResultMarkup={({ result, clickHandler }) =>
              VetPracticesSearchResult({ result, clickHandler, autoCompleteField: 'practiceName' })
            }
            searchResults={searchResults}
            onSearch={handleSearch}
            onSelect={handleSelect}
          />

          <ErrorText
            cssClass="generic_error"
            message={[
              {
                value: (
                  <div className="errorMessageContainer">
                    Sorry, we don’t yet have your vet in our database or your entry doesn’t match our records. Please
                    try again or{' '}
                    <a
                      href={CSPUrlsMap.contactUs.url}
                      className="link"
                    >
                      contact us
                    </a>{' '}
                    for help.
                  </div>
                ),
                isDisplayed: showNoResultsError && !showAlreadyLinkedError
              },
              {
                value: (
                  <div className="errorMessageContainer">
                    This vet is already linked to your account. Visit{' '}
                    <a
                      href={CSPUrlsMap.vetDetails.url}
                      className="link"
                    >
                      My vets
                    </a>{' '}
                    to check the details.
                  </div>
                ),
                isDisplayed: showAlreadyLinkedError && !showNoResultsError
              }
            ]}
          />
        </>
      )}

      {!isVetSelected && (
        <div>
          <p>
            Can&rsquo;t find your vet? We&rsquo;re still working on adding to our vet database and rest assured, any
            invoices submitted on claims from new vets will be added and be used for future claims.&nbsp;
            <a
              href={addVetFaqUrl}
              rel="noreferrer"
              className="helpCentreUrl"
            >
              Find out more.
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

VetPracticesSearch.propTypes = {
  onSelect: PropTypes.func,
  showAlreadyLinkedError: PropTypes.bool,
  setShowAlreadyLinkedError: PropTypes.func,
  /* eslint-disable react/forbid-prop-types */
  selectedPets: PropTypes.array,
  activeVet: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};
